import React, { FormEvent, useCallback, useMemo, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import {
  SessionBoxInfo,
  SquareButton,
  SubmitButton,
  TextArea,
  useBoolModalState,
  useToast,
} from 'components';
import { useAddNote, useGetSessionHistory } from '@app/api';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase * 2,
  },
  note: {
    ...theme.fonts.caption,
    whiteSpace: 'pre-wrap',
  },
  textArea: {
    marginBottom: theme.marginBase,
  },
  sumbitLine: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: theme.marginBase * 2,
  },
}));

interface Props {
  className?: string;
  sessionId?: string;
}

export const AdminNoteCard = ({ className, sessionId }: Props) => {
  const classes = useStyles();
  const toast = useToast();
  const { data: events } = useGetSessionHistory(sessionId);

  const [adding, open, close] = useBoolModalState();
  const [note, setNote] = useState('');
  const { mutateAsync: addNote } = useAddNote(sessionId);

  const sendNote = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      try {
        await addNote({ message: note });
        close();
      } catch (e) {
        toast.exception(e);
      }
    },
    [note, close],
  );

  const notes = useMemo(() => {
    return (
      events
        ?.filter((event) => event.eventName === 'NoteAdded')
        .map((event) => ({
          id: event.id,
          message: event.metadata.message,
        })) || []
    );
  }, [events]);

  return (
    <SessionBoxInfo
      className={className}
      color="purple"
      title="Notes"
      icon="ri-sticky-note-line"
      actionButton={
        <SquareButton
          onClick={adding ? close : open}
          color="purple"
          icon="ri-add-line"
        />
      }
    >
      {!!adding && (
        <form onSubmit={sendNote}>
          <TextArea
            containerClass={classes.textArea}
            onChange={(e) => setNote(e.currentTarget.value)}
          />
          <div className={classes.sumbitLine}>
            <SubmitButton small>Send</SubmitButton>
          </div>
        </form>
      )}
      <div className={classes.container}>
        {notes.map((note) => (
          <p key={note.id} className={classes.note}>
            {note.message}
          </p>
        ))}
      </div>
    </SessionBoxInfo>
  );
};
